import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Spinner,
} from "../../design-system";
import { Icon } from "../../design-system/Icon";
import {
  acceptInvitation,
  activateInvitation,
  getInvitationByToken,
  getMe,
} from "../../helpers/kronaly_helper";
import { getLoggedinUser, setAuthorization } from "../../helpers/api_helper";
import { formatInstant } from "../../helpers/datetime";

const ROLE_LABEL = {
  owner: "Propietario",
  admin: "Administrador",
  manager: "Manager",
  employee: "Empleado",
};

const STATUS_BLOCKED = ["accepted", "revoked", "expired"];

function unwrap(r) {
  if (r && typeof r === "object" && "data" in r) return r.data;
  return r;
}

// Caducidad de la invitación en la zona del workspace (tz llega en la
// respuesta pública de la invitación; la página no tiene contexto /me).
function formatExpiry(iso, tz) {
  if (!iso) return null;
  return formatInstant(iso, tz, {
    day: "2-digit", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function AcceptInvitationPage() {
  document.title = "Invitación | Kronaly";
  const { token } = useParams();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  // Activación (cuenta `invited` que aún debe fijar contraseña).
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Si hay sesión activa, cargamos /me en paralelo para saber si el
      // email coincide. Si no, sólo cargamos la invitación pública.
      const u = getLoggedinUser();
      if (u?.access_token) setAuthorization(u.access_token);

      const invRes = await getInvitationByToken(token);
      const inv = unwrap(invRes);
      setInvitation(inv);

      if (u?.access_token) {
        try {
          const meRes = await getMe();
          setMe(unwrap(meRes));
        } catch (_) {
          // Sesión expirada → tratamos como anónimo.
          setMe(null);
        }
      }
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudo cargar la invitación.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const onAccept = async () => {
    setAccepting(true);
    try {
      const r = await acceptInvitation(token);
      const data = unwrap(r) || {};
      if (data.workspace_id) {
        localStorage.setItem("kronaly_workspace_id", data.workspace_id);
      }
      setAccepted(true);
      // Damos al usuario un instante para ver el éxito y redirigimos.
      setTimeout(() => navigate("/dashboard"), 1200);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudo aceptar la invitación.");
      setAccepting(false);
    }
  };

  const onActivate = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== password2) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setActivating(true);
    try {
      await activateInvitation(token, password);
      setActivated(true);
      // Ya tiene contraseña: le mandamos a iniciar sesión con su email
      // precargado (decisión de producto: no auto-login desde el enlace).
      const email = encodeURIComponent(invitation?.email || "");
      setTimeout(
        () => navigate(`/login?email=${email}`),
        1500,
      );
    } catch (err) {
      setError(
        typeof err === "string"
          ? err
          : err?.message || "No se pudo activar la cuenta.",
      );
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <Page>
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <Spinner />
        </div>
      </Page>
    );
  }

  if (accepted) {
    return (
      <Page>
        <Card style={{ padding: 32, textAlign: "center" }}>
          <Icon name="CheckCircle2" size={48} style={{ color: "var(--kr-success-600)" }} />
          <h2 style={{ margin: "16px 0 8px", color: "var(--kr-fg-strong)" }}>¡Bienvenido!</h2>
          <p style={{ color: "var(--kr-fg-muted)" }}>
            Ya formas parte de <strong>{invitation?.workspace_name}</strong>. Te llevamos al
            dashboard…
          </p>
        </Card>
      </Page>
    );
  }

  if (activated) {
    return (
      <Page>
        <Card style={{ padding: 32, textAlign: "center" }}>
          <Icon name="CheckCircle2" size={48} style={{ color: "var(--kr-success-600)" }} />
          <h2 style={{ margin: "16px 0 8px", color: "var(--kr-fg-strong)" }}>
            Cuenta activada
          </h2>
          <p style={{ color: "var(--kr-fg-muted)" }}>
            Ya puedes acceder a <strong>{invitation?.workspace_name}</strong>. Te llevamos al
            inicio de sesión…
          </p>
        </Card>
      </Page>
    );
  }

  if (error && !invitation) {
    return (
      <Page>
        <EmptyState
          icon="MailX"
          title="Invitación no válida"
          description={error}
          action={{ label: "Ir al inicio", icon: "ArrowRight", onClick: () => navigate("/") }}
        />
      </Page>
    );
  }

  if (!invitation) return null;

  const status = invitation.status;
  if (STATUS_BLOCKED.includes(status)) {
    const blockedDesc = {
      accepted: "Esta invitación ya fue aceptada. Si necesitas volver a entrar, pide al administrador que te invite de nuevo.",
      revoked: "Esta invitación fue revocada. Si crees que es un error, contacta con el administrador.",
      expired: "El enlace de invitación ha caducado. Pide al administrador que te reenvíe una nueva invitación.",
    }[status];
    return (
      <Page>
        <EmptyState
          icon={status === "expired" ? "Clock" : "MailX"}
          title={
            status === "accepted"
              ? "Invitación ya aceptada"
              : status === "revoked"
                ? "Invitación revocada"
                : "Invitación caducada"
          }
          description={blockedDesc}
          action={{ label: "Ir al inicio", icon: "ArrowRight", onClick: () => navigate("/") }}
        />
      </Page>
    );
  }

  // Logged-in flow
  if (me) {
    const meMatches = (me.email || "").toLowerCase() === (invitation.email || "").toLowerCase();
    if (!meMatches) {
      return (
        <Page>
          <InvitationCard invitation={invitation}>
            <Alert tone="warning">
              Estás conectado como <strong>{me.email}</strong>, pero esta invitación es para{" "}
              <strong>{invitation.email}</strong>. Cierra sesión y vuelve a entrar con el email
              correcto.
            </Alert>
            <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
              <Button variant="ghost" onClick={() => navigate("/dashboard")}>
                Volver al dashboard
              </Button>
              <Button
                variant="primary"
                icon="LogOut"
                onClick={() => navigate(`/logout?next=/invitations/${token}`)}
              >
                Cerrar sesión
              </Button>
            </div>
          </InvitationCard>
        </Page>
      );
    }

    return (
      <Page>
        <InvitationCard invitation={invitation}>
          {error && <Alert tone="danger">{error}</Alert>}
          <p style={{ color: "var(--kr-fg-muted)", marginBottom: 16 }}>
            Vas a unirte a <strong>{invitation.workspace_name}</strong> como{" "}
            <strong>{ROLE_LABEL[invitation.role] || invitation.role}</strong>.
          </p>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="ghost" onClick={() => navigate("/dashboard")} disabled={accepting}>
              Ahora no
            </Button>
            <Button variant="primary" icon="Check" onClick={onAccept} disabled={accepting}>
              {accepting ? "Aceptando…" : "Aceptar invitación"}
            </Button>
          </div>
        </InvitationCard>
      </Page>
    );
  }

  // Anonymous flow
  const next = `/invitations/${encodeURIComponent(token)}`;

  // Cuenta ya existente en estado `invited` (típico: empleado creado sin
  // invitar). No tiene contraseña todavía → la fija aquí mismo, sin pasar
  // por /register ni /login.
  if (invitation.requires_activation) {
    return (
      <Page>
        <InvitationCard invitation={invitation}>
          {error && (
            <div style={{ marginBottom: 12 }}>
              <Alert tone="danger">{error}</Alert>
            </div>
          )}
          <p style={{ color: "var(--kr-fg-muted)", marginBottom: 16 }}>
            Crea una contraseña para <strong>{invitation.email}</strong> y accede a{" "}
            <strong>{invitation.workspace_name}</strong>.
          </p>
          <form onSubmit={onActivate}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field label="Contraseña" required>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  autoComplete="new-password"
                  autoFocus
                />
              </Field>
              <Field label="Repite la contraseña" required>
                <Input
                  type="password"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  placeholder="Repite la contraseña"
                  autoComplete="new-password"
                />
              </Field>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <Button
                type="submit"
                variant="primary"
                icon="Check"
                disabled={activating}
              >
                {activating ? "Activando…" : "Activar cuenta"}
              </Button>
            </div>
          </form>
        </InvitationCard>
      </Page>
    );
  }

  return (
    <Page>
      <InvitationCard invitation={invitation}>
        <p style={{ color: "var(--kr-fg-muted)", marginBottom: 16 }}>
          {invitation.requires_register ? (
            <>
              Crea tu cuenta con <strong>{invitation.email}</strong> para unirte a{" "}
              <strong>{invitation.workspace_name}</strong>.
            </>
          ) : (
            <>
              Inicia sesión con <strong>{invitation.email}</strong> para unirte a{" "}
              <strong>{invitation.workspace_name}</strong>.
            </>
          )}
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {invitation.requires_register ? (
            <Button
              variant="primary"
              icon="UserPlus"
              onClick={() =>
                navigate(
                  `/register?email=${encodeURIComponent(invitation.email)}&next=${encodeURIComponent(next)}`,
                )
              }
            >
              Crear cuenta
            </Button>
          ) : (
            <Button
              variant="primary"
              icon="LogIn"
              onClick={() =>
                navigate(
                  `/login?email=${encodeURIComponent(invitation.email)}&next=${encodeURIComponent(next)}`,
                )
              }
            >
              Iniciar sesión
            </Button>
          )}
        </div>
      </InvitationCard>
    </Page>
  );
}

function Page({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--kr-bg-base)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 520, width: "100%" }}>{children}</div>
    </div>
  );
}

function InvitationCard({ invitation, children }) {
  const expiryHuman = formatExpiry(invitation.expires_at, invitation.timezone);
  return (
    <Card style={{ padding: 32 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <Icon name="UserPlus" size={20} style={{ color: "var(--kr-action)" }} />
        <h2
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            color: "var(--kr-fg-strong)",
          }}
        >
          Invitación a Kronaly
        </h2>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <Badge tone="primary">{ROLE_LABEL[invitation.role] || invitation.role}</Badge>
        <Badge tone="neutral">{invitation.workspace_name}</Badge>
      </div>
      {expiryHuman && (
        <div style={{ fontSize: 12, color: "var(--kr-fg-subtle)", marginBottom: 16 }}>
          Caduca el {expiryHuman}
        </div>
      )}
      {children}
    </Card>
  );
}
